import { focusManager } from "@tanstack/react-query";
import { get } from "api/api-methods";
import { warn } from "logger/logger";
import { WebBooking } from "types/web-booking";

/**
 * Mobile browsers drop an in-flight request when the page goes to the background,
 * most often the Chrome/Edge/Google-app family on iOS. The booking itself is
 * created, the confirmation is sent and the restaurant sees it, but the reply
 * never reaches the guest, so the finalize call looks like it failed (BOOK-3829).
 *
 * Before telling the guest their booking failed, ask the server what actually
 * happened. Getting this wrong sends them back to book all over again, which is
 * where the duplicate bookings come from.
 */

/**
 * Waits before each attempt. The first asks immediately; the later ones cover the
 * case where the connection is still down, or where the server had not finished
 * committing at the moment the guest's request was cut off.
 */
const RETRY_DELAYS_MS = [0, 750, 2500];

/** Stop waiting for a backgrounded page to come back after this long. */
const MAX_HIDDEN_WAIT_MS = 120_000;

/** Injected by the tests so they can skip the waiting and fake the server. */
interface VerifyBookingDeps {
    fetchBooking: (bookingId: string) => Promise<WebBooking>;
    waitForFocus: (timeoutMs: number) => Promise<void>;
    delay: (ms: number) => Promise<void>;
}

const fetchBookingFromApi = async (bookingId: string): Promise<WebBooking> =>
    new WebBooking(await get<WebBooking>(`webBooking/webBookings/${bookingId}`));

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Resolves as soon as the page is in front of the guest again, or after
 * `timeoutMs`, whichever comes first. React Query's focus manager already tracks
 * this (it reads `document.visibilityState`) and can be faked in tests through
 * `focusManager.setEventListener`.
 */
const waitForFocus = (timeoutMs: number): Promise<void> => {
    if (focusManager.isFocused()) return Promise.resolve();

    return new Promise<void>((resolve) => {
        const cleanUps: Array<() => void> = [];
        const stopWaiting = () => {
            cleanUps.forEach((cleanUp) => cleanUp());
            resolve();
        };

        const timer = setTimeout(stopWaiting, timeoutMs);
        cleanUps.push(() => clearTimeout(timer));
        cleanUps.push(
            focusManager.subscribe((focused) => {
                if (focused) stopWaiting();
            })
        );
    });
};

/**
 * Asks the server whether the booking went through after all.
 *
 * @returns the booking when it did, or `null` when it genuinely did not and the
 * guest should be told something went wrong.
 */
export const verifyBookingWentThrough = async (
    bookingId: string | null | undefined,
    deps: Partial<VerifyBookingDeps> = {}
): Promise<WebBooking | null> => {
    if (!bookingId) return null;

    const {
        fetchBooking = fetchBookingFromApi,
        waitForFocus: onFocus = waitForFocus,
        delay = sleep,
    } = deps;

    // The request usually died because nobody was looking at the page. Hold off
    // until they are, so we neither ask over a dead connection nor decide what to
    // show to a page that is not on screen.
    await onFocus(MAX_HIDDEN_WAIT_MS);

    let lastError: unknown;

    for (const waitMs of RETRY_DELAYS_MS) {
        if (waitMs > 0) await delay(waitMs);

        try {
            const booking = await fetchBooking(bookingId);
            if (booking.isCompleted()) return booking;
        } catch (error) {
            lastError = error;
        }
    }

    if (lastError) {
        // Could be a dropped connection or an HTTP error, so stay vague on the cause.
        warn("Could not confirm whether the booking went through", lastError);
    }

    return null;
};
